import { ProjectDto } from '@cradua/data';
import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as ProjectManagementActions from './project-management.actions';

export const PROJECT_MANAGEMENT_FEATURE_KEY = 'projectmanagement';

export interface ProjectManagementState extends EntityState<ProjectDto> {
  selectedProjectManagementId?: string | number; // which ProjectManagement record has been selected
  loaded: boolean; // has the ProjectManagement list been loaded
  error?: string | null; // last none error (if any)
}

export interface ProjectManagementPartialState {
  readonly [PROJECT_MANAGEMENT_FEATURE_KEY]: ProjectManagementState;
}

export const projectManagementAdapter: EntityAdapter<ProjectDto> = createEntityAdapter<
  ProjectDto
>({
  selectId: (projectManagement) => projectManagement.id,
  sortComparer: false,
});

export const initialState: ProjectManagementState = projectManagementAdapter.getInitialState(
  {
    // set initial required properties
    loaded: false,
    error: null,
    selectedProjectManagementId: null,
  }
);

const projectManagementReducer = createReducer(
  initialState,
  on(ProjectManagementActions.loadProjectManagement, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ProjectManagementActions.loadProjectManagementSuccess,
    (state, { projectManagement }) =>
      projectManagementAdapter.addAll(projectManagement, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ProjectManagementActions.loadProjectManagementFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(ProjectManagementActions.addProjectManagement, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ProjectManagementActions.addProjectManagementSuccess,
    (state, { newProjectManagement }) =>
      projectManagementAdapter.addOne(newProjectManagement, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ProjectManagementActions.addProjectManagementFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),

  on(ProjectManagementActions.addProjectSubManagement, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ProjectManagementActions.addProjectSubManagementSuccess,
    (state, { newProjectSubManagement }) =>
      projectManagementAdapter.upsertOne(newProjectSubManagement, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ProjectManagementActions.addProjectSubManagementFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(ProjectManagementActions.uploadExcelStudentRecord, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ProjectManagementActions.uploadExcelStudentRecordSuccess,
    (state, { uploadExcelRecord }) =>
      projectManagementAdapter.addMany(uploadExcelRecord, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ProjectManagementActions.uploadExcelStudentRecordFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(ProjectManagementActions.updateProjectManagement, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ProjectManagementActions.updateProjectManagementSuccess,
    (state, { updateProjectManagement }) =>
      projectManagementAdapter.upsertOne(updateProjectManagement, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ProjectManagementActions.updateProjectManagementFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  // search student

  on(
    ProjectManagementActions.searchProjectManagementRecord,
    (state, { query }) => {
      return {
        ...state,
        selectedProjectManagementId: query,
        loaded: false,
        error: null,
      };
    }
  ),
  on(ProjectManagementActions.removeProjectManagement, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ProjectManagementActions.removeProjectManagementSuccess,
    (state, { ids }) =>
      projectManagementAdapter.removeMany(ids, { ...state, loaded: false })
  ),
  on(
    ProjectManagementActions.removeProjectManagementFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),

  on(ProjectManagementActions.removeProjectManagementStakeholder, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ProjectManagementActions.removeProjectManagementStakeholderSuccess,
    (state, { ids }) =>
      projectManagementAdapter.removeMany(ids, { ...state, loaded: false })
  ),
  on(
    ProjectManagementActions.removeProjectManagementStakeholderFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),

  on(ProjectManagementActions.removeProjectManagementCluster, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ProjectManagementActions.removeProjectManagementClusterSuccess,
    (state, { ids }) =>
      projectManagementAdapter.removeMany(ids, { ...state, loaded: false })
  ),
  on(
    ProjectManagementActions.removeProjectManagementClusterFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),

  on(ProjectManagementActions.removeProjectManagementReportMaster, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ProjectManagementActions.removeProjectManagementReportMasterSuccess,
    (state, { ids }) =>
      projectManagementAdapter.removeMany(ids, { ...state, loaded: false })
  ),
  on(
    ProjectManagementActions.removeProjectManagementReportMasterFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),

  on(
    ProjectManagementActions.removeProjectManagementReportMeeting,
    (state) => ({
      ...state,
      loaded: true,
      error: null,
    })
  ),
  on(
    ProjectManagementActions.removeProjectManagementReportMeetingSuccess,
    (state, { ids }) =>
      projectManagementAdapter.removeMany(ids, { ...state, loaded: false })
  ),
  on(
    ProjectManagementActions.removeProjectManagementReportMeetingFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),

  on(ProjectManagementActions.removeProjectManagementMain, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ProjectManagementActions.removeProjectManagementMainSuccess,
    (state, { id }) =>
      projectManagementAdapter.removeOne(id, { ...state, loaded: false })
  ),
  on(
    ProjectManagementActions.removeProjectManagementMainFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  )
);

export function reducer(
  state: ProjectManagementState | undefined,
  action: Action
) {
  return projectManagementReducer(state, action);
}
